<template>
  <div class="analysis">
    <div class="left">
      <div v-loading="configLoading" class="analysis-card left-top">
        <div
          class="analysis-card__header"
          :class="{ 'is-expanded': analysisExpanded }"
          @click="analysisExpanded = !analysisExpanded"
        >
          <div class="analysis-card__icon">
            <i class="icon-edit2-ai-line t-iconfont"></i>
          </div>
          <div class="analysis-card__info">
            <div class="analysis-card__title">跟进内容提炼</div>
            <div class="analysis-card__desc">
              提炼内容时若满足多个适用范围，按照由上到下的优先级顺序选择规则
            </div>
          </div>
          <i
            class="analysis-card__arrow icon-arrow-up-s-line t-iconfont"
            :class="{ 'is-collapsed': !analysisExpanded }"
          ></i>
        </div>
        <Transition name="collapse">
          <div v-show="analysisExpanded" class="analysis-card__body">
            <ConfigCard class="analysis-card__panel" :config-info="configInfo" />
          </div>
        </Transition>
      </div>
      <div class="left-bottom">
        <div v-loading="qualityConfigLoading" class="quality-card">
          <div
            class="quality-card__header"
            :class="{ 'is-expanded': qualityExpanded }"
            @click="qualityExpanded = !qualityExpanded"
          >
            <div class="quality-card__icon">
              <i class="icon-ai-generate-2 t-iconfont"></i>
            </div>
            <div class="quality-card__info">
              <div class="quality-card__title">跟进质量分析</div>
              <div class="quality-card__desc">助手基于分析规则分析会话中存在的风险</div>
            </div>
            <i
              class="icon-arrow-up-s-line quality-card__arrow t-iconfont"
              :class="{ 'is-collapsed': !qualityExpanded }"
            ></i>
          </div>
          <Transition name="collapse">
            <div v-show="qualityExpanded" class="quality-card__body">
              <div class="quality-card__section">
                <div class="quality-card__section-title">分析规则</div>
                <div class="quality-card__section-desc">助手基于分析规则分析会话中存在的风险</div>
                <button
                  class="quality-card__outline-btn"
                  type="button"
                  @click.stop="openRuleDialog"
                >
                  <i class="icon-settings-4-line t-iconfont"></i>
                  <span>规则设置</span>
                </button>
              </div>
              <div class="quality-card__section quality-card__section--manual">
                <div class="quality-card__section-head">
                  <div class="quality-card__section-title">可人工校准</div>
                  <button
                    class="quality-card__switch"
                    :class="{ 'is-on': manualVerificationEnabled }"
                    type="button"
                    @click.stop="handleManualVerificationChange"
                  >
                    <span class="quality-card__switch-dot"></span>
                  </button>
                </div>
                <div class="quality-card__section-row">
                  <div class="quality-card__section-desc quality-card__section-desc--inline">
                    开启后，可人工修改 AI 对会话质量分析的结果
                  </div>
                  <a class="quality-card__link" @click.stop="openPersonSelectDialog"
                    >设置成员范围</a
                  >
                </div>
                <div class="quality-card__tags">
                  <span v-for="tag in manualVerificationTags" :key="tag" class="quality-card__tag">
                    {{ tag }}
                  </span>
                </div>
              </div>
              <div class="quality-card__invalid">
                <div class="quality-card__invalid-title">无效跟进</div>
                <div class="quality-card__invalid-desc">
                  命中设置条件后将不进行 AI 分析且标记为无效跟进
                </div>
                <div
                  v-for="(item, index) in invalidFollowUpOptions"
                  :key="index"
                  class="quality-card__option"
                >
                  <el-checkbox
                    v-model="item.checked"
                    class="quality-card__checkbox"
                    @change="handleInvalidFollowUpCheckChange(index)"
                  />
                  <span class="quality-card__option-text">{{ item.prefix }}</span>
                  <input
                    v-model="item.value"
                    class="quality-card__input"
                    type="text"
                    @input="onInvalidFollowUpOptionInput(index, item.value)"
                  />
                  <span class="quality-card__option-text">{{ item.suffix }}</span>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
    <div class="right">
      <div class="preview-panel">
        <div class="preview-panel__header">
          <span class="preview-panel__header-title">预览</span>
          <span class="preview-panel__header-hint">预览为示例数据，仅供参考</span>
        </div>
        <div class="preview-panel__body">
          <div class="preview-result-card">
            <div class="preview-result-card__title-row">
              <div class="preview-result-card__ai-icon">
                <img alt="AI" class="preview-result-card__ai-img" src="@/assets/logo.svg" />
              </div>
              <span class="preview-result-card__title">质量分析</span>
              <a v-show="manualVerificationEnabled" class="preview-result-card__calibrate"
                >人工校准</a
              >
            </div>
            <div class="preview-result-card__content">
              <div class="preview-result-card__field-row">
                <span class="preview-result-card__field-label">质量结果</span>
                <div class="preview-result-card__field-value">
                  <span class="preview-result-card__risk-dot"></span>
                  <span class="preview-result-card__field-text">疑似风险</span>
                </div>
              </div>
              <div class="preview-result-card__summary-list">
                <div class="preview-result-card__summary-item">
                  <span class="preview-result-card__time-tag">00:00:00</span>
                  <p class="preview-result-card__summary-text">
                    1.
                    客户明确表示我方价格显著高于竞品，且其可接受的预算远低于我方报价，存在明显的价格敏感性问题，可能影响最终成交概率。（原文概要：客户提到菜鸟报价为1200元/年，蓝色报价为一千多或两千元左右，而我方报价为3600元/年，客户多次表示"这么贵"）
                  </p>
                </div>
                <div class="preview-result-card__summary-item">
                  <span class="preview-result-card__time-tag">00:15:23</span>
                  <p class="preview-result-card__summary-text">
                    2.
                    客户需求较为简单，仅需基础客户跟进功能，暗示对高阶功能无强需求，可能认为当前产品性价比不高。（原文概要：客户提到菜鸟报价为1200元/年，蓝色报价为一千多或两千元左右，而我方报价为3600元/年，客户多次表示"这么贵"）
                  </p>
                </div>
                <div class="preview-result-card__summary-item">
                  <span class="preview-result-card__time-tag">01:21:22</span>
                  <p class="preview-result-card__summary-text">
                    3.
                    客户提及多个竞品，且明确指出其价格优势，表明已有一定倾向，存在被低价竞品吸引的风险。（原文概要：客户提到菜鸟报价为1200元/年，蓝色报价为一千多或两千元左右，而我方报价为3600元/年，客户多次表示"这么贵"）
                  </p>
                </div>
              </div>
              <div class="preview-result-card__suggestion">
                <div class="preview-result-card__suggestion-header">
                  <div class="preview-result-card__ai-icon preview-result-card__ai-icon--small">
                    <img alt="AI" class="preview-result-card__ai-img" src="@/assets/logo.svg" />
                  </div>
                  <span class="preview-result-card__suggestion-title">AI 建议</span>
                </div>
                <p class="preview-result-card__suggestion-text">
                  针对客户提出的价格顾虑，可结合客户公司规模（仅三四名业务员），考虑是否有更灵活的套餐方案（如低于
                  10 人规模的短期套餐），或强调 618 活动（两年赠 8
                  个月）的性价比，量化系统对业绩提升的帮助（如多成交 1-2
                  单即可覆盖成本），降低客户对价格的敏感度。<br />及时跟进客户内部商量结果，在活动到期前（618
                  最后一天）主动联系，提醒活动福利，推动客户尽快决策，避免因拖延导致意向流失。<br />若客户仍对价格有顾虑，可考虑提供试用期或简化版功能套餐作为过渡，先让客户体验系统价值，为后续升级或续约铺垫。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Dialog
    v-model:show="dialogRuleVisible"
    :close-on-click-modal="false"
    :max-height="800"
    :max-width="1008"
    :min-height="600"
    :min-width="800"
    title="设置分析规则"
  >
    <div class="analysis-rule">
      <Rule ref="ruleRef" @afterSave="closeRuleDialog"></Rule>
    </div>
    <template #footer>
      <el-button class="dialog-btn" @click="closeRuleDialog">取消</el-button>
      <el-button class="dialog-btn" color="#7a52f4" :loading="loadingSave" @click="handleSave"
        >确定</el-button
      >
    </template>
  </Dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import xbbSdk from 'xbb-sdk'
import xbb from '@xbb/xbb-utils'
import {
  getMeetingAssistantConfig,
  updateMeetingAssistantConfig,
  getInvalidConfig,
  updateInvalidConfig
} from '@/api'
import Dialog from '@/components/layout/dialog-layout/index.vue'
import Rule from '@/pages/ai-board/session-set/components/quality-analysis/rule.vue'
import ConfigCard from './config-card.vue'
import { usePersonSelectStore } from '@/store/personSelectStore'

const personSelectStore = usePersonSelectStore()
const PAGE_DATA_TYPE = 3
const QUALITY_ANALYSIS_TYPE = 2

const defaultConfig = {
  callCenterEnable: 0,
  ykMobileEnable: 0
}

const defaultQualityConfig = {
  manualVerification: 0,
  permission: [],
  wordCountLimit: 0
}

const configLoading = ref(false)
const qualityConfigLoading = ref(false)
const loadingSave = ref(false)
const analysisExpanded = ref(true)
const qualityExpanded = ref(true)
const dialogRuleVisible = ref(false)
const ruleRef = ref(null)
const isMessageListenerAdded = ref(false)
const manualVerificationTags = ref([])
const invalidFollowUpOptions = ref([
  {
    prefix: '「跟进记录」沟通内容 ＜',
    value: 0,
    suffix: '个字时，不触发 AI 分析且标记为无效跟进',
    checked: true,
    enableKey: 'wordCountLimitEnable',
    valueKey: 'wordCountLimit'
  },
  {
    prefix: '「呼叫中心」通话时长 ＜',
    value: 0,
    suffix: '秒时，不触发 AI 分析且标记为无效跟进',
    checked: false,
    enableKey: 'recordingTimeLimitEnable',
    valueKey: 'callCenterTimeLimit'
  },
  {
    prefix: '「云客手机」电话时长 ＜',
    value: 0,
    suffix: '秒时，不触发 AI 分析且标记为无效跟进',
    checked: true,
    enableKey: 'ykCallTimeLimitEnable',
    valueKey: 'ykCallTimeLimit'
  }
])
const configInfo = ref({
  config: { ...defaultConfig }
})
const qualityConfigInfo = ref({
  config: { ...defaultQualityConfig }
})
// 生成唯一实例ID
const instanceId = ref(xbb.guid())

const manualVerificationEnabled = computed(() => {
  return qualityConfigInfo.value?.config?.manualVerification === 1
})

const updateInvalidConfigField = async (payload) => {
  try {
    await updateInvalidConfig(payload)
    ElMessage.success('更新成功')
  } catch (error) {
    getInvalidConfigInfo()
  }
}

const handleInvalidFollowUpCheckChange = async (index) => {
  const option = invalidFollowUpOptions.value[index]
  if (!option?.enableKey) return

  await updateInvalidConfigField({
    [option.enableKey]: option.checked ? 1 : 0
  })
}

const onInvalidFollowUpOptionInput = xbb.debounce(async (index, val) => {
  const option = invalidFollowUpOptions.value[index]
  if (!option?.valueKey) return

  if (val === '') {
    invalidFollowUpOptions.value[index].value = 0
  } else {
    invalidFollowUpOptions.value[index].value = Math.min(+String(val).replace(/[^\d]/g, ''), 9999)
  }

  await updateInvalidConfigField({
    [option.valueKey]: invalidFollowUpOptions.value[index].value
  })
})

const getConfigInfo = async () => {
  try {
    configLoading.value = true
    const { result } = await getMeetingAssistantConfig({
      type: PAGE_DATA_TYPE
    })

    if (result?.data) {
      const config = JSON.parse(result.data.config || '{}')
      configInfo.value = {
        ...result.data,
        config: {
          ...defaultConfig,
          ...config
        }
      }
    }
  } catch (error) {
    console.error('获取提炼规则配置失败', error)
  } finally {
    configLoading.value = false
  }
}

const getQualityConfigInfo = async () => {
  try {
    qualityConfigLoading.value = true
    const { result } = await getMeetingAssistantConfig({
      type: QUALITY_ANALYSIS_TYPE
    })

    if (result?.data) {
      qualityConfigInfo.value = {
        ...result.data,
        config: {
          ...defaultQualityConfig,
          ...JSON.parse(result.data.config || '{}')
        }
      }
      manualVerificationTags.value =
        qualityConfigInfo.value.config.permission?.map((item) => item.name).filter(Boolean) || []
    }
  } catch (error) {
    console.error('获取质量分析配置失败', error)
  } finally {
    qualityConfigLoading.value = false
  }
}

const updateQualityConfigInfo = async (permission) => {
  try {
    qualityConfigLoading.value = true
    await updateMeetingAssistantConfig({
      id: qualityConfigInfo.value?.id,
      type: QUALITY_ANALYSIS_TYPE,
      config: JSON.stringify({
        manualVerification: qualityConfigInfo.value.config.manualVerification,
        permission: permission || qualityConfigInfo.value.config.permission,
        wordCountLimit: qualityConfigInfo.value.config.wordCountLimit || 0
      })
    })
    await getQualityConfigInfo()
    ElMessage.success('质量分析配置更新成功')
  } catch (error) {
    console.error('更新质量分析配置失败', error)
  } finally {
    qualityConfigLoading.value = false
  }
}

const getInvalidConfigInfo = async () => {
  try {
    const res = await getInvalidConfig()
    const {
      wordCountLimitEnable,
      wordCountLimit,
      ykCallTimeLimitEnable,
      ykCallTimeLimit,
      recordingTimeLimitEnable,
      callCenterTimeLimit
    } = res.result || {}

    invalidFollowUpOptions.value = [
      {
        ...invalidFollowUpOptions.value[0],
        checked: wordCountLimitEnable === 1,
        value: wordCountLimit ?? 0
      },
      {
        ...invalidFollowUpOptions.value[1],
        checked: recordingTimeLimitEnable === 1,
        value: callCenterTimeLimit ?? 0
      },
      {
        ...invalidFollowUpOptions.value[2],
        checked: ykCallTimeLimitEnable === 1,
        value: ykCallTimeLimit ?? 0
      }
    ]
  } catch (error) {
    console.error('获取无效跟进配置失败', error)
  }
}

const handleManualVerificationChange = () => {
  qualityConfigInfo.value.config.manualVerification = manualVerificationEnabled.value ? 0 : 1
  updateQualityConfigInfo()
}

const openRuleDialog = () => {
  dialogRuleVisible.value = true
}

const closeRuleDialog = async () => {
  dialogRuleVisible.value = false
}

const handleSave = async () => {
  loadingSave.value = true
  try {
    await ruleRef.value?.updateTheQualityAnalysisRule()
  } catch (error) {
    console.error('更新分析规则失败', error)
    throw error
  } finally {
    loadingSave.value = false
  }
}

const openPersonSelectDialog = () => {
  // 设置当前实例为活动实例
  personSelectStore.setActiveInstance(instanceId.value)
  xbbSdk.sendMessage('personSelect', {
    activeTabName: 'dept',
    showTabsName: ['dept', 'user', 'role'],
    defaultVal: xbb.deepClone(qualityConfigInfo.value?.config?.permission || []),
    otherParams: {
      deptVisibleRule: true,
      parentStrictly: true
    }
  })
}

const getMessageFormXbb = (event) => {
  const { data, type } = event.data || {}
  // 非当前活动实例则不处理消息
  if (type !== 'personSelectResult' || personSelectStore.getActiveInstance() !== instanceId.value)
    return
  if (type === 'personSelectResult' && data?.length) {
    updateQualityConfigInfo(data)
  }

  // 处理完成后清除活动实例状态
  personSelectStore.clearActiveInstance()
}

onMounted(() => {
  getConfigInfo()
  getQualityConfigInfo()
  getInvalidConfigInfo()
  if (!isMessageListenerAdded.value) {
    window.addEventListener('message', getMessageFormXbb)
    isMessageListenerAdded.value = true
  }
})

onBeforeUnmount(() => {
  if (isMessageListenerAdded.value) {
    window.removeEventListener('message', getMessageFormXbb)
    isMessageListenerAdded.value = false
  }
})
</script>

<style lang="scss" scoped>
.analysis {
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  justify-content: center;

  .left {
    width: 588px;
    box-sizing: border-box;
  }

  .left-bottom {
    margin-top: 12px;
  }

  .right {
    margin-left: 12px;
    width: 520px;
    height: 100%;
    min-height: 0;
  }
}
.analysis-rule {
  :deep(.rule) {
    overflow: unset;
  }
  :deep(.is-free) {
    // textarea
    .el-textarea {
      .el-textarea__inner {
        padding: 16px 4px 0 20px;
        font-size: 16px;
        line-height: 28px;
      }
    }
  }
}

.analysis-card {
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);

  &__header {
    display: flex;
    align-items: center;
    padding: 12px;
    box-sizing: border-box;
    cursor: pointer;

    &.is-expanded {
      border-bottom: 1px solid #e5e6eb;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 12px;
    background: #ffffff;
    border-radius: 8px;

    .t-iconfont {
      font-size: 20px;
      color: #323233;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: #323233;
  }

  &__desc {
    margin-top: 4px;
    font-size: 14px;
    line-height: 20px;
    color: #969799;
  }

  &__arrow {
    margin-left: 12px;
    font-size: 24px;
    color: #969799;
    transition: transform 0.2s ease;

    &.is-collapsed {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding: 16px;
    box-sizing: border-box;
  }

  &__panel {
    :deep(.config-card) {
      margin-top: 0;
    }
    :deep(.rule-sub-tab) {
      margin-bottom: 16px;
    }
  }
}
$quality-card-bg: #ffffff; // 填充/纯白
$quality-card-border: #e5e6eb; // 线条/Line-2 一般线条
$quality-card-title: #323233; // 文字/Text-4 强调正文标题
$quality-card-subtitle: #646566; // 文字/Text-3 次强调正文标题
$quality-card-desc: #969799; // 文字/Text-2 次要信息
$quality-card-link: #248df5; // 链接/Link-6 常规
$quality-card-tag-bg: #f2f3f5; // 填充/Fill-2 一般 常规 白底悬浮
$quality-card-switch: #7a52f4; // 拓展色/紫罗兰/Violets-6
$quality-card-shadow: 0 6px 16px rgba(0, 0, 0, 0.08); // 三级投影/高
$quality-card-placeholder: #c8c9cc; // 文字/Text-1 置灰信息

.quality-card {
  overflow: hidden;
  background: $quality-card-bg;
  border: 1px solid $quality-card-border;
  border-radius: 12px;
  box-shadow: $quality-card-shadow;

  &__header {
    display: flex;
    align-items: center;
    padding: 12px;
    box-sizing: border-box;
    cursor: pointer;

    &.is-expanded {
      border-bottom: 1px solid $quality-card-border;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 12px;
    background: $quality-card-bg;
    border-radius: 8px;

    .t-iconfont {
      font-size: 20px;
      color: $quality-card-title;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: $quality-card-title;
  }

  &__desc {
    margin-top: 4px;
    font-size: 14px;
    line-height: 20px;
    color: $quality-card-desc;
  }

  &__arrow {
    margin-left: 12px;
    font-size: 24px;
    color: $quality-card-desc;
    transition: transform 0.2s ease;

    &.is-collapsed {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding: 16px;
    box-sizing: border-box;
  }

  &__section {
    & + & {
      margin-top: 16px;
    }
  }

  &__section-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $quality-card-title;
  }

  &__section-desc {
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
    color: $quality-card-desc;
  }

  &__outline-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    margin-top: 8px;
    padding: 0 8px;
    background: $quality-card-bg;
    border: 1px solid $quality-card-border;
    border-radius: 8px;
    box-sizing: border-box;
    color: $quality-card-subtitle;
    cursor: pointer;

    .t-iconfont {
      margin-right: 8px;
      font-size: 16px;
      color: $quality-card-subtitle;
    }

    span {
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__section--manual {
    margin-top: 16px;
  }

  &__section-head {
    display: flex;
    align-items: center;
  }

  &__switch {
    position: relative;
    width: 30px;
    height: 16px;
    margin-left: 12px;
    padding: 0;
    background: $quality-card-placeholder;
    border: 0;
    border-radius: 100px;
    cursor: pointer;
    transition: background 0.2s ease;

    &.is-on {
      background: $quality-card-switch;

      .quality-card__switch-dot {
        left: 16px;
      }
    }
  }

  &__switch-dot {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    background: $quality-card-bg;
    border-radius: 50%;
    transition: left 0.2s ease;
  }

  &__section-row {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }

  &__section-desc--inline {
    margin-top: 0;
  }

  &__link {
    margin-left: 8px;
    font-size: 14px;
    line-height: 20px;
    color: $quality-card-link;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
  }

  &__tags {
    margin-top: 4px;
    font-size: 0;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    height: 20px;
    margin-top: 4px;
    margin-right: 8px;
    padding: 0 8px;
    background: $quality-card-tag-bg;
    border-radius: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $quality-card-title;
    box-sizing: border-box;
  }

  &__invalid {
    margin-top: 16px;
  }

  &__invalid-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $quality-card-title;
  }

  &__invalid-desc {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $quality-card-desc;
  }

  &__option {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }

  &__checkbox {
    margin-right: 8px;
    line-height: 1;
  }

  &__option-text {
    font-size: 14px;
    line-height: 20px;
    color: $quality-card-title;
  }

  &__input {
    width: 64px;
    height: 32px;
    margin: 0 8px;
    padding: 0 12px;
    background: $quality-card-bg;
    border: 1px solid $quality-card-border;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 14px;
    line-height: 20px;
    color: $quality-card-title;
    outline: none;

    &::placeholder {
      color: $quality-card-placeholder;
    }
  }
}

.dialog-btn {
  border-radius: 8px;
}

// preview panel — right column
.preview-panel {
  width: 520px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);

  &__header {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f2f3f5;
    border-bottom: 1px solid #e5e6eb;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  &__header-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: #323233;
    margin-right: 12px;
  }

  &__header-hint {
    font-size: 12px;
    line-height: 16px;
    color: #969799;
  }

  &__body {
    flex: 1;
    min-height: 0;
    padding: 16px;
    overflow-y: auto;
    background: #f2f3f5;
    box-sizing: border-box;
  }
}

.preview-result-card {
  background: linear-gradient(144deg, #e9ecfa 0%, #dfeeff 100%);
  border: 1px solid #ffffff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;

  &__title-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  &__ai-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 30px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  &__ai-icon--small {
    width: 20px;
    height: 26px;
  }

  &__ai-img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }

  &__title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #323233;
  }

  &__calibrate {
    font-size: 14px;
    line-height: 20px;
    color: #248df5;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
  }

  &__content {
    background: #ffffff;
    border-radius: 8px;
    padding: 16px;
    box-sizing: border-box;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  }

  &__field-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  &__field-label {
    font-size: 14px;
    line-height: 20px;
    color: #969799;
    margin-right: 12px;
    white-space: nowrap;
  }

  &__field-value {
    display: flex;
    align-items: center;
  }

  &__risk-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 4px;
    background: #fc5555;
    margin-right: 8px;
    flex-shrink: 0;
  }

  &__field-text {
    font-size: 14px;
    line-height: 20px;
    color: #323233;
  }

  &__summary-list {
    display: flex;
    flex-direction: column;
  }

  &__summary-item {
    display: flex;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__time-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    padding: 2px 8px;
    background: #faf3ff;
    border-radius: 4px 24px 24px 4px;
    font-size: 12px;
    line-height: 16px;
    color: #7a52f4;
    white-space: nowrap;
    flex-shrink: 0;
    margin-right: 12px;
    box-sizing: border-box;
  }

  &__summary-text {
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    margin: 0;
  }

  &__suggestion {
    margin-top: 16px;
    background: #f7f8fa;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    padding: 12px;
    box-sizing: border-box;
  }

  &__suggestion-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }

  &__suggestion-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #323233;
  }

  &__suggestion-text {
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    margin: 0;
  }
}
</style>
